import React, { useState } from 'react';
import api from '../../utils/api';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { useToast } from '../../hooks/use-toast';
import MobileInput from '../ui/mobile-input';
import { validateMobile } from '../../utils/phoneValidation';
import { SUPPORT_EMAIL } from './landingData';
import LandingSection from './LandingSection';
import AsyncSubmitButton from '../ui/AsyncSubmitButton';

const LAPTOP_IMG = `${process.env.PUBLIC_URL}/images/landing/laptop-demo.svg`;

export default function LandingEnquiry() {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    schoolName: '',
    message: '',
  });
  const [phoneError, setPhoneError] = useState('');

  const resetForm = () => {
    setForm({ name: '', phone: '', email: '', schoolName: '', message: '' });
  };

  const submit = async ({ beginLoading }) => {
    const err = validateMobile(form.phone);
    if (err) {
      setPhoneError(err);
      toast({ title: 'Invalid mobile number', description: err, variant: 'destructive' });
      return;
    }
    setPhoneError('');
    beginLoading();
    try {
      const res = await api.post('/crm/inquiries/public', {
        school_name: form.schoolName || form.name || 'Website Lead',
        email: form.email,
        contact_number: form.phone || null,
        message: form.message,
        remarks: form.message,
      });
      const ok = res.data?.success !== false;
      if (!ok) throw new Error(res.data?.message || 'Failed to submit enquiry');
      toast({ title: 'Enquiry sent', description: 'Our team will contact you soon.' });
    } catch (err) {
      toast({
        title: 'Failed',
        description: err.response?.data?.message || err?.message || 'Could not send enquiry.',
        variant: 'destructive',
      });
      throw err;
    }
  };

  return (
    <LandingSection
      id="contact"
      title="Ready to modernize your school?"
      subtitle="Book a free demo and see how School Analytics can transform your operations."
      innerClassName="mt-8 grid items-center gap-8 lg:grid-cols-2 lg:gap-12"
    >
        <div className="relative order-2 flex justify-center lg:order-1">
          <div className="pointer-events-none absolute inset-0 rounded-3xl bg-blue-500/10 blur-3xl" />
          <img
            src={LAPTOP_IMG}
            alt="School analytics dashboard on laptop"
            className="relative w-full max-w-sm lg:max-w-md"
            loading="lazy"
          />
        </div>

        <Card className="order-1 border-white/10 bg-white/5 shadow-[0_24px_80px_rgba(0,0,0,0.35)] lg:order-2">
          <CardHeader>
            <CardTitle className="text-white">Book a Free Demo</CardTitle>
            <CardDescription className="text-white/50">
              Share your details and our team will respond within 24 hours.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={(e) => e.preventDefault()} className="space-y-4">
              <div>
                <Label className="text-white/80">Name</Label>
                <Input
                  className="border-white/10 bg-white/5 text-white placeholder:text-white/30"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label className="text-white/80">Phone</Label>
                <MobileInput
                  className="border-white/10 bg-white/5 text-white"
                  placeholder="Enter your mobile number"
                  value={form.phone}
                  onChange={(v) => {
                    setForm({ ...form, phone: v });
                    setPhoneError('');
                  }}
                  error={phoneError}
                />
              </div>
              <div>
                <Label className="text-white/80">Email</Label>
                <Input
                  type="email"
                  className="border-white/10 bg-white/5 text-white placeholder:text-white/30"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label className="text-white/80">School Name</Label>
                <Input
                  className="border-white/10 bg-white/5 text-white placeholder:text-white/30"
                  value={form.schoolName}
                  onChange={(e) => setForm({ ...form, schoolName: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label className="text-white/80">Message</Label>
                <Textarea
                  className="min-h-[100px] border-white/10 bg-white/5 text-white placeholder:text-white/30"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Tell us more about your school/requirements"
                  required
                />
              </div>
              <AsyncSubmitButton
                onAction={submit}
                onSuccess={resetForm}
                className="w-full bg-blue-600 hover:bg-blue-700"
                loadingLabel="Sending…"
              >
                Send Enquiry
              </AsyncSubmitButton>
              <p className="text-center text-xs text-white/40">
                or email us directly at{' '}
                <a className="text-blue-400 underline" href={`mailto:${SUPPORT_EMAIL}`}>
                  {SUPPORT_EMAIL}
                </a>
              </p>
            </form>
          </CardContent>
        </Card>
    </LandingSection>
  );
}
